import axios from "axios";

// ======================================================
// 🌐 BASE CONFIG
// ======================================================
export const API_BASE = process.env.REACT_APP_API_BASE;
// Default to externalDB if not explicitly defined
export const DB_NAME = process.env.REACT_APP_DB_NAME?.trim();
console.log("🌐 Using database:", DB_NAME);


// ======================================================
// 🔑 AUTH HELPERS (JWT)
// ======================================================
export const hasToken = () => !!localStorage.getItem("admin-auth");

export const getAuthHeader = () => {
  const token = localStorage.getItem("admin-auth");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ======================================================
// 🧭 API ENDPOINTS
// ======================================================
export const ENDPOINTS = {
  // --- PRODUCTS ---
  PRODUCTS: `${API_BASE}/getproducts`,
  PRODUCT_CREATE: `${API_BASE}/products/create`,
  PRODUCT_UPDATE_PRICE: `${API_BASE}/products/prices`,
  GET_PRODUCTS: `${API_BASE}/getproducts`,
  PRODUCT_CATEGORIES: `${API_BASE}/categories`,
  PRODUCT_HSN: `${API_BASE}/hsn`,
  CUSTOMERS: `${API_BASE}/customers`, 

  // --- PURCHASES ---
  PURCHASES: `${API_BASE}/purchases`,
  PURCHASE_RETURN: `${API_BASE}/purchase-returns`,
  PURCHASE_RETURN_ITEMS: `${API_BASE}/purchase-returns/items`,

  // --- SUPPLIERS ---
  SUPPLIERS: `${API_BASE}/suppliers`,
  SUPPLIERS_TYPE: `${API_BASE}/supplier-types`,

  // --- UNITS ---
  UNITS: `${API_BASE}/units`,

  // --- USERS / SALES / PROFITS ---
  USERS: `${API_BASE}/users/Getusers`,
  SALES: `${API_BASE}/sales`,
  SALE_DETAILS: `${API_BASE}/sales/:id`,
  PROFITS: `${API_BASE}/profits`,

  // --- STOCK LEDGER / INVENTORY ADJUSTMENT ---
  STOCK_LEDGER: `${API_BASE}/stock-ledger`,
  INVENTORY_ADJUSTMENTS: `${API_BASE}/stock-ledger`,
  TEAM_MEMBERS: `${API_BASE}/team-members`,
  PORTFOLIOS: `${API_BASE}/portfolios`, // Added Portfolios node
  INQUIRIES: `${API_BASE}/inquiries`, // Added Inquiries node
  RECRUITMENT: `${API_BASE}/recruitment`, // Added Recruitment node
};

// ======================================================
// ⚙️ GENERIC REQUEST WRAPPER
// ======================================================
async function request(
  method,
  endpoint,
  { params = {}, data = {}, db = DB_NAME, timeout = 10000 } = {}
) {
  if (!hasToken()) throw new Error("User not authenticated");

  try {
    const config = {
      method,
      url: endpoint,
      headers: getAuthHeader(),
      params: db ? { db, ...params } : params,
      data,
      timeout,
    };

    const response = await axios(config);
    console.info(`[API] ✅ ${method.toUpperCase()} ${endpoint}`, response.status);
    return response.data;
  } catch (err) {
    const errMsg = err.response?.data?.error || err.response?.data?.message || err.message;
    console.error(`[API] ❌ ${method.toUpperCase()} ${endpoint}:`, errMsg);
    throw new Error(errMsg);
  }
}

// ======================================================
// 🧰 GENERIC METHODS
// ======================================================
export const get = (endpoint, params = {}, db) => request("get", endpoint, { params, db });
export const post = (endpoint, data = {}, db) => request("post", endpoint, { data, db });
export const put = (endpoint, data = {}, db) => request("put", endpoint, { data, db });
export const del = (endpoint, db) => request("delete", endpoint, { db });

// ======================================================
// 📦 PRODUCTS
// ======================================================
export const fetchProducts = async (params = {}, db = DB_NAME) => {
  try {
    const res = await get(ENDPOINTS.GET_PRODUCTS, params, db);
    return Array.isArray(res) ? res : res?.products || [];
  } catch (err) {
    console.error("fetchProducts failed:", err.message);
    return [];
  }
};

export const createProduct = (data = {}, db = DB_NAME) =>
  post(ENDPOINTS.PRODUCT_CREATE, data, db);

export const updateProductPrice = (id, price, db = DB_NAME) =>
  put(`${ENDPOINTS.PRODUCT_UPDATE_PRICE}/${id}`, { prices: price }, db);

export const fetchTeamMembers = async () => {
  try {
    const res = await get(ENDPOINTS.TEAM_MEMBERS, {}, undefined);
    return Array.isArray(res) ? res : res?.teamMembers || res?.data || [];
  } catch (err) {
    console.error("fetchTeamMembers failed:", err.message);
    throw err;
  }
};

export const createTeamMember = async (data) => {
  try {
    return await post(ENDPOINTS.TEAM_MEMBERS, data, undefined);
  } catch (err) {
    console.error("createTeamMember failed:", err.message);
    throw err;
  }
};

export const deleteTeamMember = async (id) => {
  try {
    return await del(`${ENDPOINTS.TEAM_MEMBERS}/${id}`, undefined);
  } catch (err) {
    console.error("deleteTeamMember failed:", err.message);
    throw err;
  }
};

// Fetch all accounts
export const fetchAccounts = async (db = DB_NAME) => {
  try {
    const res = await get(`${API_BASE}/accounts`, {}, db);
    console.log("fetchAccounts response:", res);

    if (Array.isArray(res)) return res;          
    if (Array.isArray(res?.data)) return res.data; 
    if (Array.isArray(res?.accounts)) return res.accounts; 
    return [];
  } catch (err) {
    console.error("fetchAccounts error:", err.message);
    return [];
  }
};

// ======================================================
// 🧾 PURCHASES
// ======================================================
export const fetchPurchases = (params = {}, db = DB_NAME) => get(ENDPOINTS.PURCHASES, params, db);
export const fetchPurchaseReturns = (params = {}, db = DB_NAME) =>
  get(ENDPOINTS.PURCHASE_RETURN, params, db);
export const fetchPurchaseReturnItems = (purchaseId, db = DB_NAME) =>
  get(ENDPOINTS.PURCHASE_RETURN_ITEMS, { purchaseId }, db);
export const createPurchaseReturn = (data = {}, db = DB_NAME) =>
  post(ENDPOINTS.PURCHASE_RETURN, data, db);

// ======================================================
// 👥 SUPPLIERS
// ======================================================
export const fetchSuppliers = (params = {}, db = DB_NAME) => get(ENDPOINTS.SUPPLIENS, params, db);
export const fetchSupplierTypes = (params = {}, db = DB_NAME) =>
  get(ENDPOINTS.SUPPLIERS_TYPE, params, db);

// ======================================================
// ⚖️ UNITS / CATEGORY / HSN
// ======================================================
export const fetchUnits = (params = {}, db = DB_NAME) => get(ENDPOINTS.UNITS, params, db);
export const fetchCategories = (params = {}, db = DB_NAME) =>
  get(ENDPOINTS.PRODUCT_CATEGORIES, params, db);
export const fetchHSN = (params = {}, db = DB_NAME) => get(ENDPOINTS.PRODUCT_HSN, params, db);

// ======================================================
// 🏬 STOCK LEDGER / INVENTORY ADJUSTMENTS
// ======================================================
export const postStockAdjustment = (data, db = DB_NAME) =>
  post(ENDPOINTS.INVENTORY_ADJUSTMENTS, data, db);

export const fetchStockLedger = (params = {}, db = DB_NAME) =>
  get(ENDPOINTS.STOCK_LEDGER, params, db);
export const fetchTransactions = fetchStockLedger;

// ======================================================
// 👨‍💼 USERS / SALES / PROFITS
// ======================================================
export const fetchUsers = async (db = DB_NAME) => {
  if (!hasToken()) return null;
  try {
    return await get(ENDPOINTS.USERS, {}, db);
  } catch (err) {
    console.warn("fetchUsers failed:", err.message);
    return null;
  }
};

export const fetchSales = (params = {}, db = DB_NAME) => get(ENDPOINTS.SALES, params, db);
export const fetchSaleDetails = (id, db = DB_NAME) =>
  get(ENDPOINTS.SALE_DETAILS.replace(":id", id), {}, db);

export const fetchProfits = async (salesInvoiceItemId = null, db = DB_NAME) => {
  try {
    const params = salesInvoiceItemId ? { salesInvoiceItemId } : {};
    const res = await get(ENDPOINTS.PROFITS, params, db);
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    console.warn("Unexpected profit response format:", res);
    return [];
  } catch (err) {
    console.error("fetchProfits error:", err.message);
    return [];
  }
};

// ======================================================
// 🧹 UTILITIES
// ======================================================
export const deduplicateById = (arr) =>
  Array.from(new Map(arr.map((item) => [item.id, item])).values());

// ======================================================
// 🔍 OTHER HELPERS
// ======================================================
export const fetchHsnCodes = async (db = DB_NAME) => {
  try {
    const res = await axios.get(process.env.REACT_APP_API_HSN, {
      headers: getAuthHeader(),
      params: { db },
    });
    return res.data || [];
  } catch (err) {
    console.error("fetchHsnCodes error:", err.message);
    return [];
  }
};

export const fetchCompany = async (db = DB_NAME) => {
  try {
    const res = await axios.get(process.env.REACT_APP_API_COMPANY, {
      headers: getAuthHeader(),
      params: { db },
    });
    return res.data || null;
  } catch (err) {
    console.error("fetchCompany error:", err.message);
    return null;
  }
};

export const fetchProductGst = async (productId, db = DB_NAME) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_PRODUCTS_BASE}/${productId}/gst`,
      {
        headers: getAuthHeader(),
        params: { db },
      }
    );
    return res.data || null;
  } catch (err) {
    console.error("fetchProductGst error:", err.message);
    return null;
  }
};

// Fetch ledger for a specific account
export const fetchLedger = async (accountId, db = DB_NAME) => {
  try {
    const res = await get(`${process.env.REACT_APP_API_BASE}/ledger/${accountId}`, {}, db);
    return res;
  } catch (err) {
    console.error(`[API] ❌ GET ledger/${accountId}`, err.message);
    return { transactions: [], account: null };
  }
};

// Fetch Cash or Bank Book for a specific account
export const fetchCashOrBankBook = async (accountId, type = "cash", db = DB_NAME) => {
  if (!accountId) return [];
  const endpoint = type === "cash" ? "cashbook" : "bankbook";
  try {
    const res = await get(`${process.env.REACT_APP_API_BASE}/ledger/${endpoint}/${accountId}`, {}, db);
    return type === "cash" ? res.cashBook : res.bankBook;
  } catch (err) {
    console.error(`[API] ❌ GET ledger/${endpoint}/${accountId}`, err.message);
    return [];
  }
};

// ======================================================
// 💼 PORTFOLIOS
// ======================================================
export const fetchPortfolios = async () => {
  try {
    const res = await get(ENDPOINTS.PORTFOLIOS, {}, undefined);
    return res;
  } catch (err) {
    console.error("fetchPortfolios failed:", err.message);
    throw err;
  }
};

export const createPortfolio = async (payload) => {
  try {
    return await post(ENDPOINTS.PORTFOLIOS, payload, undefined);
  } catch (err) {
    console.error("createPortfolio failed:", err.message);
    throw err;
  }
};

export const updatePortfolio = async (id, payload) => {
  try {
    return await put(`${ENDPOINTS.PORTFOLIOS}/${id}`, payload, undefined);
  } catch (err) {
    console.error("updatePortfolio failed:", err.message);
    throw err;
  }
};

export const deletePortfolio = async (id) => {
  try {
    return await del(`${ENDPOINTS.PORTFOLIOS}/${id}`, undefined);
  } catch (err) {
    console.error("deletePortfolio failed:", err.message);
    throw err;
  }
};